import {
    consentCookieNames,
    inquiryType,
    monthNames,
    dayNames,
    queryString,
    enableNewRegistrationPopUp,
    approvalAmountLocalStorageName,
    popupListDetailLoanApprovalEstimateModal,
    bannerAdPositions
} from '@Components/Utilities/SandhillsConstants';
import translator from '@Components/Utilities/Translator';
import jsonpAdapter from 'axios-jsonp';
import axios from 'axios';
import qs from 'qs';
import { getCookie, setLocalStorageValue, setSessionValue } from '@Components/Utilities/CookieHelper';
import * as SandhillsImpervaAjax from '@sandhills/imperva-ajax';

/**
 * All react network requests should use this method.
 * @param {string} url (url of the requested resource - the base url will be prepended for hosted site requests)
 * @param {string} method (method type of the request i.e. post, get, etc. - defaults to get)
 * @param {object} data (data to be sent in the request i.e. string, plain object, etc.)
 * @param {object} headers (custom headers to be sent with the request)
 * @param {string} responseType (type of data the server will respond with i.e. json or jsonp - defaults to json)
 * @param {object} responseSchema (if true the full response of the request will be returned, otherwise only the data from the server will be returned - defaults to false)
 * @param {object} cancelToken (CancelToken object that can be used to cancel the request)
 * @param {object} sendAntiForgeryHeader if false then we skip sending the antiforgery token header, default is true (set to false when making requests to non-mvc site i.e. VPNCheck)
 */
const sandhillsRequest = ({
    url = '',
    method = 'get',
    data = {},
    headers = {},
    responseType = 'json',
    responseSchema = false,
    cancelToken = null,
    sendAntiForgeryHeader = true,
    disableHeaders = false,
    withCredentials = false,
    errorHandling = {
        message: 'An error occurred. Please refresh your page and try again.',
        showDetails: false,
        useConsole: true,
        useAlert: true
    }
} = {}) => {

    const siteSettings = sandhills.TradeSites.React.SiteSettings;
    setWindowOverrides();

    let params = {};
    let adapter = responseType == 'jsonp' ? jsonpAdapter : null;
    let baseURL = window.Overrides.baseUrl ? 'https:' + window.Overrides.baseUrl : '';
    let defaultSiteLangCode = sandhills.TradeSites.React.ComponentProps['defaultSiteLangCode'] ? sandhills.TradeSites.React.ComponentProps['defaultSiteLangCode'] : 'en-US';
    let userLanguage = sandhills.TradeSites.React.ComponentProps['langCode'] ? sandhills.TradeSites.React.ComponentProps['langCode'] : defaultSiteLangCode;
    let userCountryCode = sandhills.TradeSites.React.ComponentProps['countryCode'] ? sandhills.TradeSites.React.ComponentProps['countryCode'] : '';

    if ((sandhills.TradeSites.React.ComponentProps['hasLangSelector'] && userLanguage != defaultSiteLangCode) && !data?.languageCode) {
        url = removeURLParameter(url, queryString.Language);
        url = insertURLParameter(url, queryString.Language, userLanguage);
    }

    if (userCountryCode != '' && !data?.countryCode) {
        url = removeURLParameter(url, queryString.CountryCode);
        url = insertURLParameter(url, queryString.CountryCode, userCountryCode);
    }

    if (method == 'get') {
        if (Object.keys(data).length) {
            params = { ...data };
        }
    } else if (method == 'post') {
        let token = document.getElementsByName('__RequestVerificationToken')?.[0]?.value;
        if (data instanceof FormData) {
            data.append('__RequestVerificationToken', token);
        } else {
            data.__RequestVerificationToken = token;
            data = qs.stringify(data);
        }
    }

    if (sendAntiForgeryHeader) {
        headers[antiXsrfToken.key] = antiXsrfToken.value;
    }

    // Since cookies can't be passed for cross-domain requests, passing through headers
    if (window?.Overrides?.isHostedSite) {
        let cookieBotCookie = getCookie(consentCookieNames.CookieBot);
        let cookieYesCookie = getCookie(consentCookieNames.CookieYes);
        let osanoCookie = getCookie(consentCookieNames.OsanoUnencrypted);

        if (osanoCookie) {
            headers[consentCookieNames.OsanoUnencrypted] = `${consentCookieNames.OsanoUnencrypted}=${encodeURIComponent(osanoCookie)}`;
        } else if (cookieBotCookie) {
            headers[consentCookieNames.CookieBot] = `${consentCookieNames.CookieBot}=${encodeURIComponent(cookieBotCookie)}`;
        } else if (cookieYesCookie) {
            headers[consentCookieNames.CookieYes] = `${consentCookieNames.CookieYes}=${encodeURIComponent(cookieYesCookie)}`;
        }
    }

    //Add Cors Test QS
    if (siteSettings.UseImpervaAjax) {
        if (!headers) {
            headers = {};
        }

        headers[SandhillsImpervaAjax.Imperva?.tokenHeaderName] = SandhillsImpervaAjax.Imperva?.getToken();
    }

    return axios({
        url: url,
        baseURL: baseURL,
        method: method,
        data: data,
        params: params,
        headers: disableHeaders ? null : headers,
        adapter: adapter,
        cancelToken: cancelToken,
        withCredentials: withCredentials
    })
        .then(response => {
            if (siteSettings.UseImpervaAjax) {
                const result = SandhillsImpervaAjax.Imperva?.analyzeResponse(response);
                return result ? response : result;
            }

            return response;
        })
        .then(response => {
            return responseSchema ? response : response.data;
        }).catch(error => {
            if (siteSettings.UseImpervaAjax) {
                const result = error?.response ? error?.response : false;
                SandhillsImpervaAjax.Imperva?.analyzeResponse(result);
            }
            if (errorHandling.useConsole) {
                if (errorHandling.showDetails) {
                    console.error(errorHandling.message, error);
                }
                else {
                    console.error(errorHandling.message);
                }
            }

            if (errorHandling.useAlert) {
                let msg = errorHandling.message;
                if (errorHandling.showDetails) {
                    msg += ' Error Details: ' + error;
                }

                alert(msg);
            }
        });
};

// Simply executes a request with the given URL and parameters without expecting a return so we can see it in the page logs
const sandhillsLogging = (url, parameters) => {
    try {
        let baseUrl = window.Overrides.baseUrl ? 'https:' + window.Overrides.baseUrl : '';
        if (baseUrl.endsWith("/")) {
            baseUrl = baseUrl.slice(0, -1);
        }

        for (let key in parameters) {
            url = insertURLParameter(url, key, parameters[key], true);
        }
        // Making use of sendBeacon to avoid blocking the main thread and to ensure delivery even if the page unloads
        navigator.sendBeacon(baseUrl + url);
    }
    catch (error) {
        console.error(`Error occurred in sandhillsLogging: ${error}`);
    }

}

const chunkArray = (myArray, numCols, removeChildren) => {

    if (!numCols || numCols == 0) numCols = 1;
    let arrayLength = myArray.length;
    let remainder = arrayLength % numCols;
    let chunk_size = Math.floor(arrayLength / numCols);
    let tempArray = [];

    if (remainder > 0) {
        chunk_size += 1;
    }

    for (var index = 0; index < arrayLength; index += chunk_size) {
        let endIndex = index + chunk_size;
        let chunk = myArray.slice(index, endIndex);

        if (removeChildren) {
            chunk = chunk.filter((cat) => !cat.IsChild);
        }

        if (remainder > 0) {
            remainder--;
            if (remainder == 0) {
                index++;
                chunk_size--;
            }
        }
        tempArray.push(chunk);
    }

    return tempArray;
};

const chunkByBreakingIndexes = (myArray, indexes) => {
    var list = [];
    var length = indexes.length;
    for (let i = 0; i < length; i++) {
        var start = i == 0 ? i : indexes[i - 1] + 1;
        var end = indexes[i] + 1;
        list.push(myArray.slice(start, end));

        if (i == (length - 1)) {
            start = end;
            list.push(myArray.slice(start));
        }
    }
    return list;
}

const getDistinct = (array) => {
    if (array?.length > 1) {
        return [...new Set(array)];
    }

    return array;
};

const setWindowOverrides = () => {
    if (!window.Overrides) {
        window.Overrides = {};
    }

    window.Overrides = {
        ...window.Overrides,
        baseUrl: window.Overrides.baseUrl ? window.Overrides.baseUrl : ''
    };
};
const gtmAlertPushEvent = (event, dimensionKey1, value1, dimensionKey2, value2) => {
    if (event) {
        let dataLayerEvent = { 'event': event };
        if (dimensionKey1 && value1) {
            dataLayerEvent[dimensionKey1] = value1;
        }
        if (dimensionKey2 && value2) {
            dataLayerEvent[dimensionKey2] = value2;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerEvent);
    }
};
const gtmPushEvent = (event, dimensionKey, value) => {
    if (event) {
        let dataLayerEvent = { 'event': event };
        if (dimensionKey && value) {
            dataLayerEvent[dimensionKey] = value;
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerEvent);
    }
};

const criteoPushConversion = (model) => {
    try {
        if (model.ShouldPushCriteoEvent && model.selectedInquiry != inquiryType.EmailThis && !model.IsParts &&
            model.EquipmentMessageID > 0 && model.ListingID > 0) {
            window.criteo_q = window.criteo_q || [];

            var eventDetails = [
                { event: "setAccount", account: 56820 },
                { event: "setSiteType", type: getCriteoDeviceType() },
                // It has been decided to make the email unavailable and leave blank (discuss with Alex or Nathan before adding email)
                { event: "setEmail", email: '' }
            ];

            if (model.PostalCode) {
                eventDetails.push({ event: "setZipcode", zipcode: model.PostalCode });
            }

            eventDetails.push({
                event: "trackTransaction",
                id: model.EquipmentMessageID,
                item: [
                    { id: model.ListingID, price: model.CriteoPrice ? model.CriteoPrice : 0, quantity: 1 }
                ]
            });

            window.criteo_q.push(...eventDetails);
        }
    } catch (error) {
        console.log(`Error occurred in criteoPushConversion: ${error}`)
    }
}

const criteoPushEvent = (viewType, listingIds, detailListingId) => {
    window.criteo_q = window.criteo_q || [];
    let viewTypeObject = { event: viewType };

    if (viewType == "viewList") {
        viewTypeObject = { event: viewType, item: listingIds };
    } else if (viewType == "viewItem") {
        viewTypeObject = { event: viewType, item: detailListingId };
    }

    window.criteo_q.push(
        { event: "setAccount", account: 56820 },
        { event: "setEmail", email: "" },
        { event: "setSiteType", type: getCriteoDeviceType() },
        viewTypeObject
    );
}

const showSiteJabber = (currentUser, listingID) => {

    window.dataLayer = window.dataLayer || [];
    sessionStorage.setItem('siteJabberShown', 'true');

    /* Add DLV for GTM */
    window.dataLayer.push(
        {
            'event': 'userFirstName',
            'userFirstName': currentUser.UserFirstName
        },
        {
            'event': 'userLastName',
            'userLastName': currentUser.UserLastName
        },
        {
            'event': 'userEmail',
            'userEmail': currentUser.UserEmail
        },
        {
            'event': 'orderID',
            'orderID': listingID
        }
    );

    /* Use this to trigger GTM to load the site jabber modal */
    window.dataLayer.push({
        event: 'ratingForm',
        action: 'RatingForm'
    });
}


const addAuctionSiteDLVs = ({
    listingId,
    eventId,
    eventName,
    sellerId,
    sellerName,
    userID,
    gtmEvent,
    bidAmount,
    bidValue
}) => {
    try {

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(
            {
                'event': 'auctionID',
                'auctionID': eventId
            },
            {
                'event': 'auctionName',
                'auctionName': eventName 
            },
            {
                'event': 'sellerID',
                'sellerID': sellerId
            },
            {
                'event': 'sellerName',
                'sellerName': sellerName
            },
            {
                'event': 'listingID',
                'listingID': listingId 
            },
            {
                'event': 'userID',
                'userID': userID
            }
        );

        if (gtmEvent) {
            gtmAlertPushEvent(gtmEvent, 'bidAmount', bidAmount, 'value', bidValue);
        }
    } catch (err) {
        console.error(`addAuctionSiteDLVs failed: ${err}`);
    }
}

const getCriteoDeviceType = () => {
    return /iPad/.test(navigator.userAgent) ? "t" : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent) ? "m" : "d";
}

const getGtmEvent = (inquiry) => {
    switch (inquiry) {
        case inquiryType.Offer:
            return 'sam-make-an-offer';
        case inquiryType.EmailThis:
            return 'sam-email-this';
        case inquiryType.Email:
            return 'sam-general-inquiry';
        case inquiryType.VideoChat:
            return 'sam-video-chat';
        case inquiryType.DiscussFinancing:
            return 'sam-check-availability-discuss-financing';
        case inquiryType.AskQuestion:
            return 'sam-check-availability-ask-a-question';
        case inquiryType.RequestPhotos:
            return 'sam-check-availability-request-more-photos';
        case inquiryType.CheckAvailability:
            return 'sam-check-availability-check-availability';
        default:
            return 'sam-general-inquiry';
    }
};

const getLoanApprovalEstimate = (yib, cr, pmls, ac) => {
    let yearsInBusiness = Number(yib);
    let creditRange = Number(cr);
    let priorMaxLoanSize = Number(pmls);
    let assetClass = Number(ac);
    let compMultiplier = 0.75;
    let approvalAmount = yearsInBusiness * creditRange * priorMaxLoanSize * assetClass * compMultiplier;
    if (approvalAmount > 100000) {
        approvalAmount = 100000;
    } else {
        approvalAmount = Math.ceil(approvalAmount / 100) * 100;
    }
    return approvalAmount;
};

const setLoanApprovalEstimateNewRegistrant = () => {
    if (enableNewRegistrationPopUp && !localStorage.getItem(approvalAmountLocalStorageName)) {
        setLocalStorageValue(popupListDetailLoanApprovalEstimateModal, true);
    }
};

const getDateString = (addDays, useTranslations) => {
    let date = new Date();
    date.setDate(date.getDate() + addDays);
    if (useTranslations) {
        return translator.translate('Sandhills.CRM.DSCRM.' + dayNames[date.getUTCDay()]) + ' - ' + translator.translate('sandhills.crm.dscrm.' + monthNames[date.getUTCMonth()]) + ' ' + date.getUTCDate();
    } else {
        return dayNames[date.getUTCDay()] + ' - ' + monthNames[date.getUTCMonth()] + ' ' + date.getUTCDate();
    }
};

const formatPrintPageDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, hourCycle: 'h12' };
    const date = new Date(dateString);
    return date.toLocaleString(undefined, options);
}

const truncateString = (input, maxLength) => {
    if (input && input.length > maxLength) {
        return input.substring(0, maxLength - 3) + '...';
    }
    return input;
};

const stringEqualsIgnoreCase = (str1, str2) => {
    return str1?.toUpperCase() === str2?.toUpperCase();
};

const focusErroredField = (errors, inputReferences) => {
    if (errors) {
        const errorKeys = Object.keys(errors);
        if (errorKeys?.length > 0) {
            const key = errorKeys.find(key => errors[key] && key != 'Error');
            if (key) {
                inputReferences.current?.[key]?.focus?.();
            }
        }
    }
};

const getUserSettingsSelectorCurrenciesListAsync = (sessionStorageKey, currentList, url) => {
    return new Promise((resolve, reject) => {
        if (!currentList || currentList.length <= 0) {
            let currenciesFromSession = window.sessionStorage.getItem(sessionStorageKey);
            if (currenciesFromSession) {
                resolve(JSON.parse(currenciesFromSession));
            } else {
                sandhillsRequest({
                    url: url,
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((ajaxCurrencies) => {
                    setSessionValue(sessionStorageKey, JSON.stringify(ajaxCurrencies));
                    resolve(ajaxCurrencies);
                }).catch((error) => {
                    reject(error.status + ': ' + error.statusText);
                });
            }
        } else {
            let currenciesFromSession = window.sessionStorage.getItem(sessionStorageKey);
            if (!currenciesFromSession) {
                setSessionValue(sessionStorageKey, JSON.stringify(currentList));
            }
            resolve(currentList);
        }
    });
};

const delayPromise = (ms) => {
    return (x) => new Promise(resolve => setTimeout(() => resolve(x), ms));
};

const getOuterHeight = (elem, includeMargin) => {
    let height = elem.offsetHeight;
    if (includeMargin) {
        let style = getComputedStyle(elem);
        height += parseInt(style.marginTop) + parseInt(style.marginBottom);
    }
    return height;
};

const getOuterWidth = (elem, includeMargin) => {
    let width = elem.offsetWidth;
    if (includeMargin) {
        let style = getComputedStyle(elem);
        width += parseInt(style.marginLeft) + parseInt(style.marginRight);
    }
    return width;
};

const hasSecondaryPricing = (pricingInfo) => {
    return (pricingInfo.ShowPreAuctionTime && !pricingInfo.ShowWholesalePriceOnly) ||
        (pricingInfo.ShowWholeSale && !pricingInfo.IsExclusiveWholeSale)
        || (pricingInfo.PriceGroup && pricingInfo.PriceGroup.length > 0);
};


const toSlug = (parameter, underscore) => {
    if (parameter === null || parameter.match(/^ *$/) !== null) {
        return parameter;
    }
    let slug = parameter.toString().toLowerCase().trim().replace(/ & /g, ' and ').replace(/\//g, ' slash ').replace(/-/g, ' ');
    if (underscore) {
        slug = slug.replace(/\s+/g, '_');
    }
    return slug.replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
};

const getURLParameter = (url, key) => {
    if (url) {
        let urlParts = url.split('?');
        if (urlParts.length > 1) {
            let parameters = qs.parse(urlParts[urlParts.length - 1]);
            return parameters[key] || '';
        }
    }
    return '';
};

const insertURLParameter = (url, key, value, encodeURI = true) => {
    if (url) {
        let urlParts = url.split('?');
        if (urlParts.length == 1) {
            return url + '?' + key + '=' + encodeURIComponent(value);
        } else {
            let parameters = qs.parse(urlParts[urlParts.length - 1]);
            parameters[key] = value;
            let queryString = qs.stringify(parameters);
            if (!encodeURI) {
                queryString = decodeURIComponent(queryString);
            }
            if (urlParts.length > 2) {
                url = '';
                for (let index = 0; index <= urlParts.length - 2; index++) {
                    url += index > 0 ? '?' : '';
                    url += urlParts[index];
                }
                url += (queryString ? '?' + queryString : '');
            } else {
                url = urlParts[0] + (queryString ? '?' + queryString : '');
            }
        }
    }
    return url;
};

const removeURLParameters = (url, parameters) => {
    if (url && parameters) {
        for (const parameter of parameters) {
            url = removeURLParameter(url, parameter);
        }
    }
    return url;
};

const removeURLParameter = (url, parameter) => {
    if (url) {
        let urlParts = url.split('?');
        if (urlParts.length > 1) {
            let parameters = qs.parse(urlParts[urlParts.length - 1]);
            delete parameters[parameter];
            let queryString = qs.stringify(parameters);
            if (urlParts.length > 2) {
                url = '';
                for (let index = 0; index <= urlParts.length - 2; index++) {
                    url += index > 0 ? '?' : '';
                    url += urlParts[index];
                }
                url += (queryString ? '?' + queryString : '');
            } else {
                url = urlParts[0] + (queryString ? '?' + queryString : '');
            }
        }
    }
    return url;
};

const bodyOrHtml = () => {
    if ('scrollingElement' in document && document.scrollingElement) return document.scrollingElement;
    if (navigator.userAgent.indexOf('WebKit') != -1) return document.body;
    return document.documentElement;
};

const getClientWidth = () => {
    return bodyOrHtml().clientWidth;
};

const getScrollTop = () => {
    return bodyOrHtml().scrollTop;
};

const setScrollTop = (pixels) => {
    bodyOrHtml().scrollTop = pixels;
};

const setScrollPosition = () => {
    window.scrollposition = bodyOrHtml().scrollTop;
    document.getElementsByTagName('body')?.[0]?.classList?.add('background-fixed');
    document.getElementsByClassName('body-wrapper')?.[0]?.setAttribute('style', 'margin: -' + (window.scrollposition) + 'px 0 0 0;');
};

const unsetScrollPosition = () => {
    document.getElementsByTagName('body')?.[0]?.classList?.remove('background-fixed');
    document.getElementsByClassName('body-wrapper')?.[0]?.removeAttribute('style');
    bodyOrHtml().scrollTop = window.scrollposition;
};



const repositionCookieBanner = (mobileMenuClass, windowSize) => {
    let cookieBanner = document.getElementsByClassName('cookie-banner')?.[0];
    let footer = document.getElementsByTagName('footer')?.[0];
    if (cookieBanner && footer) {
        if (bodyOrHtml().clientWidth < windowSize) {
            let mobileMenu = document.getElementsByClassName(mobileMenuClass)?.[0];
            let mobileMenuHeight = mobileMenu?.offsetHeight;
            if (mobileMenuHeight) {
                let mobileMenuHeightPx = mobileMenuHeight + 'px';
                footer.style.paddingBottom = mobileMenuHeightPx;
                cookieBanner.style.bottom = mobileMenuHeightPx;
            }
        } else {
            footer.removeAttribute('style');
            cookieBanner.removeAttribute('style');
        }
    }
};

const shouldOpenInNewTab = (url) => {
    return url?.toLowerCase().startsWith('http');
};

const getBannerAd = (collection, position) => {
    if (collection?.length > 0) {
        switch (position) {
            case bannerAdPositions.Top:
                for (let index = 0; index < collection.length; index++) {
                    let adUnit = collection[index];
                    switch (adUnit.AdGroup) {
                        case 'Homepage_Top':
                        case 'AttachmentsComponents':
                        case 'PartsDismantled':
                        case 'Jobs':
                        case 'Services':
                        case 'StandardListings':
                            return adUnit;
                            break;
                    }
                }
                break;
            case bannerAdPositions.Middle:
                for (let index = 0; index < collection.length; index++) {
                    let adUnit = collection[index];
                    switch (adUnit.AdGroup) {
                        case 'Homepage_Middle':
                        case 'AttachmentsComponents_Middle':
                        case 'PartsDismantled_Middle':
                        case 'Listings_Middle':
                            return adUnit;
                            break;
                    }
                }
                break;
            case bannerAdPositions.Bottom:
                for (let index = 0; index < collection.length; index++) {
                    let adUnit = collection[index];
                    switch (adUnit.AdGroup) {
                        case 'AttachmentsComponents_Bottom':
                        case 'PartsDismantled_Bottom':
                        case 'Listings_Bottom':
                            return adUnit;
                            break;
                    }
                }
                break;
            case bannerAdPositions.AuctionResults:
                for (let index = 0; index < collection.length; index++) {
                    let adUnit = collection[index];
                    switch (adUnit.AdGroup) {
                        case 'AuctionResults':
                            return adUnit;
                            break;
                    }
                }
                break;
            case bannerAdPositions.TopPopUp:
                for (let index = 0; index < collection.length; index++) {
                    let adUnit = collection[index];
                    switch (adUnit.AdGroup) {
                        case 'Top_PopUp':
                        case 'Top_PopUp_AuctionResults':
                            return adUnit;
                            break;
                    }
                }
                break;
        }
    }

    return null;
};
const logSAMError = (routeURL, additionalInfo) => {
    sandhillsRequest({
        url: routeURL,
        method: 'post',
        data: {
            errorMessage: additionalInfo
        },
        errorHandling: {
            useConsole: false,
            useAlert: false
        }
    });
}

const setIOSScrollLock = (isMobile) => {
    if (isMobile) {
        document.body.style.top = `-${window.scrollY}px`;
        document.body.style.position = 'fixed';
    }
    document.body.style.overflow = 'hidden';
    if (!isMobile) {
        document.body.style.paddingRight = '17px';
    }
}
const removeIOSScrollLock = (isMobile) => {
    if (isMobile) {
        const scrollY = document.body.style.top;
        document.body.style.top = '';
        document.body.style.position = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    document.body.style.overflow = '';
    if (!isMobile) {
        document.body.style.paddingRight = '';
    }
}

const convertUTCtoISODateTime = (utcDateTime) => {
    // Convert the local time zone offset from minutes to milliseconds
    const timezoneOffset = utcDateTime.getTimezoneOffset() * 60000;

    // Adjust the date by subtracting the timezone offset
    const localISOTime = new Date(utcDateTime - timezoneOffset).toISOString();

    // Remove the 'Z' to indicate it's not UTC time
    const localISOTimeString = localISOTime.slice(0, -1);

    return localISOTimeString
}

const allowModalBackgroundScroll = () => {
    //allow scrolling when modal is open
    document.body.style.overflow = null
    document.body.style.paddingRight = null;
    document.body.style.position = null;
}

const getProperTimeFormatFromMinutes = (nTimeInMinutes) => {
    var nHours = Math.floor(nTimeInMinutes / 60);
    var nMinutes = nTimeInMinutes % 60;

    var sTime = "";
    if (nHours > 0) {
        sTime += nHours.toString() + (nHours > 1 ? " " + translator.translate("sandhills.hours") + " " : " " + translator.translate("sandhills.hour") + " ");
    }
    if (nMinutes > 0) {
        sTime += nMinutes.toString() + (nMinutes > 1 ? " " + translator.translate("sandhills.minutes") + "" : " " + translator.translate("sandhills.minute") + "");
    }

    return sTime;
}

const toTitleCase = (str) => {
    if (!str) return str;
    return str.replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export {
    criteoPushConversion,
    criteoPushEvent,
    showSiteJabber,
    addAuctionSiteDLVs,
    sandhillsRequest,
    sandhillsLogging,
    getDistinct,
    setWindowOverrides,
    gtmPushEvent,
    gtmAlertPushEvent,
    getGtmEvent,
    getLoanApprovalEstimate,
    setLoanApprovalEstimateNewRegistrant,
    getDateString,
    formatPrintPageDate,
    truncateString,
    stringEqualsIgnoreCase,
    getUserSettingsSelectorCurrenciesListAsync,
    delayPromise,
    getOuterHeight,
    getOuterWidth,
    hasSecondaryPricing,
    chunkArray,
    chunkByBreakingIndexes,
    toSlug,
    getURLParameter,
    insertURLParameter,
    removeURLParameter,
    removeURLParameters,
    bodyOrHtml,
    getClientWidth,
    getScrollTop,
    allowModalBackgroundScroll,
    setScrollTop,
    setScrollPosition,
    shouldOpenInNewTab,
    unsetScrollPosition,
    repositionCookieBanner,
    getBannerAd,
    logSAMError,
    focusErroredField,
    setIOSScrollLock,
    removeIOSScrollLock,
    toTitleCase,
    convertUTCtoISODateTime,
    getProperTimeFormatFromMinutes
};